<script setup lang="ts">
import { UserMinus, Loader2, AlertTriangle } from 'lucide-vue-next';
import { ref } from 'vue';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { Member } from '@/stores/access-management';

interface Props {
  member: Member;
  orgId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  confirmed: [member: Member];
}>();

const isOpen = ref(false);
const isRemoving = ref(false);

function handleConfirm() {
  isRemoving.value = true;
  emit('confirmed', props.member);
  // Parent will handle the actual removal and close dialog
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot name="trigger">
        <Button variant="outline" size="sm">
          <UserMinus class="h-4 w-4 mr-2" />
          Remove
        </Button>
      </slot>
    </DialogTrigger>

    <DialogContent>
      <DialogHeader>
        <div class="flex items-center gap-3">
          <div class="rounded-full bg-destructive/10 p-2">
            <AlertTriangle class="h-5 w-5 text-destructive" />
          </div>
          <div>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              This action cannot be undone
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div class="py-4">
        <p class="text-sm">
          Are you sure you want to remove <strong>{{ member.email }}</strong> from this organization?
        </p>
        <p class="text-sm text-muted-foreground mt-2">
          They will lose access to all organization data and settings.
        </p>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="isOpen = false" :disabled="isRemoving">
          Cancel
        </Button>
        <Button
          variant="destructive"
          @click="handleConfirm"
          :disabled="isRemoving"
        >
          <Loader2 v-if="isRemoving" class="mr-2 h-4 w-4 animate-spin" />
          {{ isRemoving ? 'Removing...' : 'Remove Member' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
